/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registry } from '@/lib/page-designer/registry';

// STATIC_REGISTRY_START

/**
 * Initialize the static component registry.
 * This function is auto-generated by the staticRegistry Vite plugin.
 *
 * DO NOT EDIT THIS FUNCTION MANUALLY - it will be overwritten on next build.
 *
 * Components registered: Content.announcementBanner, Content.categoryTile, Content.contentAsset, Content.contentCard, Content.cssCustomizations, Content.hero, Content.heroBannerBlock, Content.heroBannerOverlay, Content.htmlCustomizations, Content.imageComponent, Content.innovationsPanel, Content.insiderBanner, Content.jsCustomizations, Content.markupContent, Content.pairsWellWith, Content.pdImage, Content.plpBanner, Content.plpSeoContent, Content.popularCategory, Content.productRecommendations, Content.productTile, Content.productTileExpanded, Content.richText, Content.spotlightPanel, Content.subCategoryImageTile, Content.videoComponent, Layout.contentPage, Layout.footerLayout, Layout.grid, Layout.heroCarousel, Layout.inGridBanners, Layout.innovationsCarousel, Layout.myJoysLayout, Layout.oneRowFourColumnPortrait, Layout.oneRowOneColumnLandscape, Layout.oneRowThreeColumnLandscape, Layout.oneRowTwoColumnLandscape, Layout.popularCategories, Layout.productCarousel, Layout.promoBar, Layout.signupSmsModalLayout, Layout.spacer, Layout.spotlightCarousel, Layout.subCategoryImageCarousel, Layout.tabbedLayout, Layout.twoUpBanner
 */
export function initializeRegistry(targetRegistry = registry): void {
    targetRegistry.registerImporter(
        'Content.announcementBanner',
        () => import('../../components/announcement-banner/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter('Content.categoryTile', () => import('../../components/category-tile/index'), {
        loader: 'loader',
    });
    targetRegistry.registerImporter('Content.contentAsset', () => import('../../components/content-asset/index'), {
        loader: 'loader',
    });
    targetRegistry.registerImporter('Content.contentCard', () => import('../../components/content-card/index'));
    targetRegistry.registerImporter(
        'Content.cssCustomizations',
        () => import('../../components/css-customizations/index')
    );
    targetRegistry.registerImporter('Content.hero', () => import('../../components/hero/index'));
    targetRegistry.registerImporter(
        'Content.heroBannerBlock',
        () => import('../../components/hero-banner-block/index')
    );
    targetRegistry.registerImporter(
        'Content.heroBannerOverlay',
        () => import('../../components/hero-banner-overlay/index')
    );
    targetRegistry.registerImporter(
        'Content.htmlCustomizations',
        () => import('../../components/html-customizations/index')
    );
    targetRegistry.registerImporter('Content.imageComponent', () => import('../../components/image/index'));
    targetRegistry.registerImporter(
        'Content.innovationsPanel',
        () => import('../../components/innovations-carousel/panel')
    );
    targetRegistry.registerImporter('Content.insiderBanner', () => import('../../components/insider-banner/index'));
    targetRegistry.registerImporter(
        'Content.jsCustomizations',
        () => import('../../components/js-customizations/index')
    );
    targetRegistry.registerImporter('Content.markupContent', () => import('../../components/markup-content/index'));
    targetRegistry.registerImporter('Content.pairsWellWith', () => import('../../components/pairs-well-with/index'), {
        loader: 'loader',
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('Content.pdImage', () => import('../../components/dynamic-image/index'));
    targetRegistry.registerImporter('Content.plpBanner', () => import('../../components/plp-banner/index'), {
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('Content.plpSeoContent', () => import('../../components/plp-seo-content/index'));
    targetRegistry.registerImporter(
        'Content.popularCategory',
        () => import('../../components/home/popular-category/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter(
        'Content.productRecommendations',
        () => import('../../components/product-recommendations/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter('Content.productTile', () => import('../../components/product-tile/index'), {
        loader: 'loader',
    });
    targetRegistry.registerImporter(
        'Content.productTileExpanded',
        () => import('../../components/product-tile-expanded/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter('Content.richText', () => import('../../components/rich-text/index'));
    targetRegistry.registerImporter('Content.spotlightPanel', () => import('../../components/spotlight/panel'));
    targetRegistry.registerImporter(
        'Content.subCategoryImageTile',
        () => import('../../components/subcategory-image-tile/index')
    );
    targetRegistry.registerImporter('Content.videoComponent', () => import('../../components/video/index'));
    targetRegistry.registerImporter('Layout.contentPage', () => import('../../components/content-page-layout/index'));
    targetRegistry.registerImporter('Layout.footerLayout', () => import('../../components/footer-layout/index'));
    targetRegistry.registerImporter('Layout.grid', () => import('../../components/grid/index'));
    targetRegistry.registerImporter('Layout.heroCarousel', () => import('../../components/hero-carousel/index'), {
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('Layout.inGridBanners', () => import('../../components/in-grid-banner/index'));
    targetRegistry.registerImporter(
        'Layout.innovationsCarousel',
        () => import('../../components/innovations-carousel/index')
    );
    targetRegistry.registerImporter('Layout.myJoysLayout', () => import('../../components/my-joys-layout/index'));
    targetRegistry.registerImporter(
        'Layout.oneRowFourColumnPortrait',
        () => import('../../components/one-row-four-column-portrait/index')
    );
    targetRegistry.registerImporter(
        'Layout.oneRowOneColumnLandscape',
        () => import('../../components/one-row-one-column-landscape/index')
    );
    targetRegistry.registerImporter(
        'Layout.oneRowThreeColumnLandscape',
        () => import('../../components/one-row-three-column-landscape/index')
    );
    targetRegistry.registerImporter(
        'Layout.oneRowTwoColumnLandscape',
        () => import('../../components/one-row-two-column-landscape/index')
    );
    targetRegistry.registerImporter(
        'Layout.popularCategories',
        () => import('../../components/home/popular-categories/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter('Layout.productCarousel', () => import('../../components/product-carousel/index'), {
        loader: 'loader',
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('Layout.promoBar', () => import('../../components/promo-bar/index'), {
        fallback: 'fallback',
    });
    targetRegistry.registerImporter(
        'Layout.signupSmsModalLayout',
        () => import('../../components/signup-modal-layout/index')
    );
    targetRegistry.registerImporter('Layout.spacer', () => import('../../components/spacer/index'));
    targetRegistry.registerImporter('Layout.spotlightCarousel', () => import('../../components/spotlight/index'));
    targetRegistry.registerImporter(
        'Layout.subCategoryImageCarousel',
        () => import('../../components/subcategory-image-carousel/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter('Layout.tabbedLayout', () => import('../../components/tabbed-layout/index'));
    targetRegistry.registerImporter('Layout.twoUpBanner', () => import('../../components/two-up-banner/index'));
}

// STATIC_REGISTRY_END
